import type { StudentReportSchema } from '@repo/schemas/report/studentReport';
import type { AxiosResponse } from 'axios';

import { isAxiosError } from 'axios';

import { axiosInstance } from '#api-client';

const PDF_MIME = 'application/pdf';

/**
 * Pulls the download filename out of Content-Disposition, handling both the
 * plain `filename="..."` form and RFC 5987 `filename*=UTF-8''...`.
 *
 * The header is only readable when the BE lists it in CORS `exposedHeaders`;
 * callers fall back to a locally built name when it isn't.
 */
const parseFilename = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) return null;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (encoded?.[1]) return decodeURIComponent(encoded[1].trim());

  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plain?.[1]?.trim() ?? null;
};

const isBinaryBody = (value: unknown): value is ArrayBuffer | ArrayBufferView => value instanceof ArrayBuffer || ArrayBuffer.isView(value);

/**
 * Axios parses the body according to responseType, not the response's own
 * content type, so a JSON error under a binary responseType arrives as raw
 * bytes and ErrorMessageHandler can't reach `data.code` — every failure would
 * degrade to the generic status-code message. Decode it in place so the shared
 * error pipeline sees the usual `{ title, status, detail, code }`.
 *
 * Hence `arraybuffer` rather than the `blob` that FileSystemService uses:
 * reading a Blob needs `Blob.text()`, which this package cannot rely on because
 * apps/native compiles it too and React Native's Blob implements only
 * size/type/slice. Bytes decode the same way everywhere. Blob is still the
 * better choice for the FS download, where payloads are large enough that
 * keeping them off the JS heap matters; a report PDF is not.
 */
const revealBinaryError = (error: unknown): never => {
  if (isAxiosError(error) && isBinaryBody(error.response?.data)) {
    try {
      error.response.data = JSON.parse(new TextDecoder().decode(error.response.data));
    } catch {
      // Not JSON (a proxy error page, a truncated body) — leave it as-is and
      // let the handler fall back to the status-code message.
    }
  }

  throw error;
};

export class ReportService {
  static async generateStudentReport(studentId: number, data: StudentReportData): Promise<StudentReportResult> {
    const response: AxiosResponse<ArrayBuffer> = await axiosInstance
      .post(`/api/report/student/${studentId}`, data, {
        responseType: 'arraybuffer',
      })
      .catch(revealBinaryError);

    const emailedHeader = response.headers['x-report-email-sent'];
    const emailed = Number(emailedHeader);

    return {
      blob: new Blob([response.data], { type: PDF_MIME }),
      filename: parseFilename(response.headers['content-disposition']),
      // Absent when CORS doesn't expose the header; null keeps that distinct
      // from a genuine "mailed nobody".
      emailedParents: emailedHeader !== undefined && Number.isFinite(emailed) ? emailed : null,
    };
  }
}

export type StudentReportData = StudentReportSchema;

export interface StudentReportResult {
  blob: Blob;
  /** From Content-Disposition; null when CORS doesn't expose the header. */
  filename: string | null;
  /** From X-Report-Email-Sent; null when the header isn't readable. */
  emailedParents: number | null;
}
